/// ビジネスデータとしての電話番号 (branded 型 + 構文バリデーション)。
///
/// 対象フィールド: `firms.phone` / `lawyers.direct_line` など。
///
/// バリデーション規則は backend `Phone` VO (`domain/src/core/value_objects.rs`) と揃える:
///   - 制御文字を含む → null
///   - 前後に whitespace がある → null (内部 space は許可: "03 (1234) 5678" は有効)
///   - `[0-9 \- ( ) +]` 以外の文字を含む → null
///   - 数字が 1 個以上必要 (セパレータのみは不可)
///   - 入力文字列をそのまま保持 (正規化なし)

/** 構文検証済みのビジネスデータ電話番号。 */
export type Phone = string & { readonly __brand: 'Phone' };

/** 電話番号として許可されるセパレータ文字。 */
const PHONE_SEPARATOR_RE = /^[0-9 \-()+]+$/;

/**
 * `value` を検証し、問題なければ `Phone` 型で返す。
 *
 * 無効な場合は `null` を返す (例外は投げない)。
 *
 * - 制御文字 (タブ・改行など) を含む場合は無効。
 * - 前後に whitespace がある場合は無効 (内部 space は許可)。
 * - 数字・`-`・` `・`(`・`)`・`+` 以外を含む場合は無効。
 * - 数字が 1 個もない場合は無効。
 */
export function createPhone(value: string): Phone | null {
  // 制御文字チェック (backend: `value.chars().any(|c| c.is_control())`)
  // is_control in Rust = General_Category Cc = U+0000-U+001F, U+007F, U+0080-U+009F。
  // sibling の email.ts / url.ts と同じ範囲を弾く (C1 制御 U+0080-U+009F を含む)。
  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0;
    if (code <= 0x1f || code === 0x7f || (code >= 0x80 && code <= 0x9f)) {
      return null;
    }
  }

  // 前後 whitespace チェック (backend: `value.trim() != value`)
  if (value !== value.trim()) {
    return null;
  }

  // 許可文字チェック: 数字と `-` ` ` `(` `)` `+` のみ
  if (!PHONE_SEPARATOR_RE.test(value)) {
    return null;
  }

  // 最低 1 個の数字が必要 (backend: `!value.chars().any(|c| c.is_ascii_digit())`)
  if (!/[0-9]/.test(value)) {
    return null;
  }

  return value as Phone;
}
